import { useTheme } from '@mui/material';
import { UrlSegments } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { MetaTags } from 'src/components';
import {
  EntityDetachedPanel,
  EntityMobilePeek,
  EntityPeekSlot,
  useEntityRecordClick,
} from 'src/components/EntitySidePanel';
import { ProjectMenu } from 'src/components/ProjectComponents/project-menu';
import { Address, Project } from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { BaseSelectedView } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { CreateButton } from 'src/pages/CustomViews/custom-views-selected-view/components/create-button';
import { useSelectedEntityParams } from 'src/pages/CustomViews/custom-views-selected-view/hooks/use-selected-entity-params';
import { ProjectSidePanel } from 'src/pages/Projects/SidePanel/side-panel';
import { useOnEsc } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';

const getProjectTitle = (project: Project) => project.name || project.address?.fullAddress;

// Mobile peek: the panel is a bare card column, so the host owns the scroller and
// the sticky tools slot (mirrors the mobile branch of SiteView/site-expanded.tsx).
const ProjectMobilePanel = ({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) => (
  <EntityMobilePeek>
    {(toolsSlot) => (
      <ProjectSidePanel project={project} onClose={onClose} toolsSlot={toolsSlot} />
    )}
  </EntityMobilePeek>
);

interface ProjectDetachedPanelProps {
  project: Project;
  onClose: () => void;
}

const ProjectDetachedPanel = ({ project, onClose }: ProjectDetachedPanelProps) => {
  const theme = useTheme();

  return (
    <EntityDetachedPanel
      icon="folder-open"
      entityColor={theme.palette.custom.OV.entity.project}
      title={getProjectTitle(project)}
      detailsCardTestId="project-details-card"
      resetKey={project.projectId}
      closeTooltip="Close Project"
      menu={
        <ProjectMenu
          address={project.address as Address}
          project={project}
          onDelete={onClose}
        />
      }
      onClose={onClose}
    >
      {(toolsSlot) => (
        <ProjectSidePanel project={project} onClose={onClose} toolsSlot={toolsSlot} />
      )}
    </EntityDetachedPanel>
  );
};

export const ProjectSelectedView = () => {
  const { isMobile } = useScreenDimensions();
  const [selectedRecord, setSelectedRecord] = useState<Project | undefined>();
  const theme = useTheme();

  const {
    record: project,
    recordId: projectId,
    openRecord,
    closeRecord,
    customView,
    viewId,
    selectedCustomViewId,
  } = useSelectedEntityParams({ entityName: EntityNames.Project });

  const selectedProject = useMemo(
    () => (projectId ? ((project as Project) ?? selectedRecord) : undefined),
    [projectId, project, selectedRecord],
  );

  const onPanelClose = useCallback(() => {
    closeRecord();
    setSelectedRecord(undefined);
  }, [closeRecord]);

  useOnEsc(onPanelClose);

  const onRecordClick = useEntityRecordClick<Project>({
    onOpenRecord: (recordProjectId) => openRecord(recordProjectId),
    onRecordSelect: setSelectedRecord,
  });

  return (
    <MetaTags
      title={
        (selectedProject && getProjectTitle(selectedProject)) || customView?.name || 'Projects'
      }
    >
      <BaseSelectedView<Project>
        title={selectedCustomViewId === UrlSegments.view.all ? 'All projects' : ''}
        entity={EntityNames.Project}
        viewId={viewId}
        selectedRecord={selectedProject}
        hasDetachedPanel={!isMobile}
        onRecordClick={onRecordClick}
        onRecordClose={() => {
          closeRecord();
          setSelectedRecord(undefined);
        }}
        sx={{
          [theme.breakpoints.up(0)]: {
            maxWidth: '100%',
          },
        }}
        slots={{
          rightPanel: !!selectedProject && (
            <EntityPeekSlot open={!!selectedProject} isMobile={isMobile}>
              {isMobile ? (
                <ProjectMobilePanel project={selectedProject} onClose={onPanelClose} />
              ) : (
                <ProjectDetachedPanel project={selectedProject} onClose={onPanelClose} />
              )}
            </EntityPeekSlot>
          ),
          createButton: <CreateButton icon="add_home_work" />,
        }}
      />
    </MetaTags>
  );
};
